import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { Icon } from '../../../icon/icon';
import { Select } from '../../../select/select';
import { TextBox } from '../../../text-box/text-box';
import {
  AddOnData,
  FilteredPointCloudClassesOptions,
  FilteredPointCloudFormatOptions,
} from '../add-ons';
import { ContentList } from './card';

type Props = {
  selected: boolean;
  spacing?: number;
  format?: FilteredPointCloudFormatOptions;
  classes?: FilteredPointCloudClassesOptions;
  onChange: (data: AddOnData) => void;
};

const FilteredPointCloudContent = (props: Props) => {
  const { t } = useTranslation('cloudProcessing');
  const theme = useTheme();
  const [pointClass, setPointClass] = React.useState<FilteredPointCloudClassesOptions>(
    props.classes ?? 'ALL',
  );
  const [exportFormat, setExportFormat] = React.useState<FilteredPointCloudFormatOptions>(
    props.format ?? 'DXF',
  );
  const [spacing, setSpacing] = React.useState<number>(props?.spacing ?? 0.02);
  const [spacingInput, setSpacingInput] = React.useState<string>(spacing.toString());

  const onClassesChanged = (value: FilteredPointCloudClassesOptions) => {
    switch (value) {
      case 'ALL':
        props.onChange({ filteredPointCloud: { classes: 'ALL' } });
        setPointClass(value);
        break;
      case 'GROUND':
        props.onChange({ filteredPointCloud: { classes: 'GROUND' } });
        setPointClass(value);
        break;
      default:
        throw Error('Invalid format');
    }
  };

  const onFormatChanged = (value: FilteredPointCloudFormatOptions) => {
    switch (value) {
      case 'LAZ':
      case 'LAS':
      case 'DXF':
        props.onChange({ filteredPointCloud: { format: value } });
        setExportFormat(value);
        break;
      default:
        throw Error('Invalid format');
    }
  };

  const onSpacingChanged = () => {
    let newSpacing = Number(spacingInput);
    if (isDefined(newSpacing)) {
      newSpacing = Math.max(0, Number(newSpacing.toFixed(3)));
      setSpacing(Math.max(0, newSpacing));
      setSpacingInput(newSpacing.toString());
      props.onChange({ filteredPointCloud: { spacing: newSpacing } });
    }
  };

  return (
    <Component>
      <ContentList>
        <li>
          <Icon
            color={theme.color.blue}
            fixedWidth={true}
            icon={['fal', 'circle-info']}
            style={{ marginTop: '0.2em' }}
          />
          <span>
            {t('setupDialog.addOns.filteredPointCloud.description', { ns: 'cloudProcessing' })}
          </span>
        </li>

        <li>
          <Icon
            color={theme.color.green}
            fixedWidth={true}
            icon={['fal', 'check']}
            style={{ marginTop: '0.2em' }}
          />
          <span>
            {t('setupDialog.addOns.filteredPointCloud.exportDataInfo', { ns: 'cloudProcessing' })}
          </span>
        </li>
      </ContentList>

      <label>
        <span>
          {t('setupDialog.addOns.filteredPointCloud.pointClassesLabel', { ns: 'cloudProcessing' })}
        </span>
        <Select
          disabled={!props.selected}
          options={
            [
              {
                id: 'ALL',
                name: t('setupDialog.addOns.filteredPointCloud.classesAll', {
                  ns: 'cloudProcessing',
                }),
              },
              {
                id: 'GROUND',
                name: t('setupDialog.addOns.filteredPointCloud.classesGround', {
                  ns: 'cloudProcessing',
                }),
              },
            ] as { id: FilteredPointCloudClassesOptions; name: string }[]
          }
          value={pointClass}
          onChange={(e) => onClassesChanged(e.target.value as FilteredPointCloudClassesOptions)}
          onClick={(e) => e.stopPropagation()}
        />
      </label>
      <label>
        <span>
          {t('setupDialog.addOns.filteredPointCloud.spacingLabel', { ns: 'cloudProcessing' })}
        </span>
        <TextBox
          disabled={!props.selected}
          step={0.001}
          type="number"
          value={spacingInput}
          onBlur={onSpacingChanged}
          onChange={(e) => setSpacingInput(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </label>
      <label>
        <span>
          {t('setupDialog.addOns.filteredPointCloud.exportFormatLabel', { ns: 'cloudProcessing' })}
        </span>
        <Select
          disabled={!props.selected}
          options={
            [{ name: 'LAS' }, { name: 'LAZ' }, { name: 'DXF' }] as {
              name: FilteredPointCloudFormatOptions;
            }[]
          }
          value={exportFormat}
          onChange={(e) => onFormatChanged(e.target.value as FilteredPointCloudFormatOptions)}
          onClick={(e) => e.stopPropagation()}
        />
      </label>
    </Component>
  );
};

const Component = styled.section`
  ul {
    margin-bottom: 1em;
  }

  label {
    span {
      font-size: 0.9em;
    }

    select {
      // TODO: Remove this styling when padding issue with select is resolved.
      padding-left: 0.25em !important;
    }

    input,
    select {
      margin: 0.5em 0 0.5em 0 !important;

      &:disabled {
        background-color: white;
        color: ${(props) => props.theme.color.gray.medium};
        border: 1px solid ${(props) => props.theme.color.gray.light};
      }
    }
  }
`;

export { FilteredPointCloudContent };
